import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Node, Edge, ReactFlowProvider, ReactFlowInstance } from 'reactflow';
import { ProcessingGraph } from '../components/graphs/ProcessingGraph';
import { GraphProvider, useGraphContext } from '../components/graphs/GraphContext';
import { GraphBookmarks } from '../components/graphs/GraphBookmarks';
import { GraphTimeline } from '../components/graphs/GraphTimeline';
import { TimelineControl } from '../components/artifacts/TimelineControl';
import * as graphService from '../services/graphService';
import { GraphFilter } from '../types/graph';

export const Graph: React.FC = () => {
  const { sessionId, lineageId } = useParams<{ sessionId?: string; lineageId?: string }>();
  const navigate = useNavigate();
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [viewMode, setViewMode] = useState<'standard' | 'hierarchical' | 'timeline'>('standard');
  const [annotations, setAnnotations] = useState<any[]>([]);
  const [timelineRange, setTimelineRange] = useState<[Date | null, Date | null]>([null, null]);
  
  const [filter, setFilter] = useState<GraphFilter>({
    nodeTypes: [],
    searchTerm: '',
  });
  
  const { data, isLoading, error } = useQuery(
    ['graph', sessionId || lineageId, filter, viewMode],
    async () => {
      if (sessionId) {
        // If filter is applied, use filterGraph, otherwise use getSessionGraph
        if (filter.nodeTypes?.length || filter.searchTerm) {
          return graphService.filterGraph(sessionId, filter);
        }
        return graphService.getSessionGraph(sessionId);
      } else if (lineageId) {
        return graphService.getLineageGraph(lineageId);
      }
      return { nodes: [], edges: [] };
    },
    { enabled: !!sessionId || !!lineageId }
  );
  
  const [currentSnapshot, setCurrentSnapshot] = useState<any>(null);
  const [previousSnapshot, setPreviousSnapshot] = useState<any>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Load initial graph data
  useEffect(() => {
    if (sessionId && viewMode !== 'timeline') {
      loadGraphData(sessionId).then(data => {
        setCurrentSnapshot({
          id: 'current',
          timestamp: new Date().toISOString(),
          description: 'Current State',
          nodes: data.nodes,
          edges: data.edges
        });
      });
    }
  }, [sessionId, viewMode, loadGraphData]);
  
  const handleSnapshotSelect = useCallback((snapshot: any) => {
    setPreviousSnapshot(currentSnapshot);
    setCurrentSnapshot(snapshot);
    setIsAnimating(true);
  }, [currentSnapshot]);
  
  const handleAnimationComplete = useCallback(() => {
    setIsAnimating(false);
  }, []);
  
  const handleExportGraph = useCallback(async () => {
    if (sessionId) {
      try {
        const imageUrl = await graphService.exportGraphAsImage(sessionId, 'png');
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `graph-${sessionId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('Failed to export graph:', err);
      }
    }
  }, [sessionId]);
  
  const handleImportLayout = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0] && sessionId) {
      const file = event.target.files[0];
      graphService.importGraphLayout(sessionId, file)
        .then(data => {
          // Handle the imported layout data
          console.log('Layout imported successfully', data);
          // You might want to refresh the graph data here
        })
        .catch(err => {
          console.error('Failed to import layout:', err);
        });
    }
  }, [sessionId]);
  
  const handleNodeClick = (node: Node) => {
    setSelectedNode(node);
  };
  
  return (
    <GraphProvider>
      <div className="graph-page">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">
              {sessionId ? 'Session Graph' : lineageId ? 'Lineage Graph' : 'Processing Graph'}
            </h1>
            <p className="text-gray-600">
              {sessionId 
                ? 'Visualizing the structure and relationships within this session' 
                : lineageId 
                  ? 'Visualizing the lineage tree of related sessions' 
                  : 'Interactive visualization of processing structure'
              }
            </p>
          </div>
          
          <div className="flex space-x-3">
            <div className="flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'standard'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${viewMode === 'standard' ? 'rounded-l-lg' : ''}`}
                onClick={() => setViewMode('standard')}
              >
                Standard View
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'hierarchical'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setViewMode('hierarchical')}
              >
                Hierarchical View
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'timeline'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setViewMode('timeline')}
              >
                Timeline View
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'collaborative'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } rounded-r-lg`}
                onClick={() => setViewMode('collaborative')}
              >
                Collaborate
              </button>
            </div>
            
            {sessionId && (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                onClick={generateShareableLink}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                </svg>
                Share
              </button>
            )}
          </div>
        </div>
        
        {/* Graph Controls */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Search nodes..."
              className="px-3 py-2 border border-gray-300 rounded-md"
              value={filter.searchTerm}
              onChange={(e) => setFilter(prev => ({ ...prev, searchTerm: e.target.value }))}
            />
            
            <select
              className="px-3 py-2 border border-gray-300 rounded-md"
              value={filter.nodeTypes?.join(',') || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFilter(prev => ({
                  ...prev,
                  nodeTypes: value ? value.split(',') : []
                }));
              }}
              multiple={false}
            >
              <option value="">All node types</option>
              <option value="session">Sessions</option>
              <option value="agent">Agents</option>
              <option value="artifact">Artifacts</option>
              <option value="phase">Phases</option>
            </select>
            
            {sessionId && (
              <GraphBookmarks
                sessionId={sessionId}
                currentFilter={filter}
                onApplyBookmark={handleApplyBookmark}
              />
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={handleExportGraph}
              disabled={!sessionId}
            >
              Export Graph
            </button>
            
            <label className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 cursor-pointer">
              Import Layout
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImportLayout}
              />
            </label>
          </div>
        </div>
        
        {viewMode === 'timeline' && sessionId && (
          <div className="mb-6">
            <GraphTimeline
              sessionId={sessionId}
              onSnapshotSelect={handleSnapshotSelect}
            />
            <div className="mt-4">
              <TimelineControl
                startDate={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)} // 1 week ago
                endDate={new Date()}
                selectedRange={timelineRange}
                onRangeChange={setTimelineRange}
              />
            </div>
          </div>
        )}
        
        {isLoading && viewMode !== 'timeline' ? (
          <div className="text-center py-10">Loading graph data...</div>
        ) : error ? (
          <div className="text-center py-10 text-red-600">Error loading graph data</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 bg-white rounded-lg shadow-md overflow-hidden" style={{ height: '700px' }}>
              <ReactFlowProvider>
                <ProcessingGraph
                  sessionId={sessionId}
                  initialNodes={data?.nodes || []}
                  initialEdges={data?.edges || []}
                  onNodeClick={handleNodeClick}
                  height="100%"
                  annotations={annotations}
                  onAddAnnotation={handleAddAnnotation}
                  onDeleteAnnotation={handleDeleteAnnotation}
                />
              </ReactFlowProvider>
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-lg font-medium mb-4">
                  {selectedNode ? 'Node Details' : 'Graph Information'}
                </h2>
                
                {selectedNode ? (
                  <div className="node-details">
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">Type:</span>
                      <span className="ml-2 font-medium">{selectedNode.type}</span>
                    </div>
                    
                    <div className="mb-3">
                      <span className="text-sm text-gray-500">ID:</span>
                      <span className="ml-2 font-medium">{selectedNode.id}</span>
                    </div>
                    
                    {selectedNode.data && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium mb-2">Properties:</h3>
                        <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-96">
                          {JSON.stringify(selectedNode.data, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    <div className="mt-4 flex justify-end">
                      <button
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        onClick={() => {
                          // Handle node expansion or navigation
                          if (selectedNode.type === 'session' && selectedNode.id) {
                            window.open(`/sessions/${selectedNode.id}`, '_blank');
                          }
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="graph-stats">
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Nodes:</span>
                      <span className="ml-2 font-medium">{data?.nodes?.length || 0}</span>
                    </div>
                    
                    <div className="mb-2">
                      <span className="text-sm text-gray-500">Edges:</span>
                      <span className="ml-2 font-medium">{data?.edges?.length || 0}</span>
                    </div>
                    
                    <div className="mt-4 text-sm text-gray-600">
                      Click on a node to view its details.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </GraphProvider>
  );
};
